/**
 * The shape a ship actually is.
 *
 * Collisions used to treat a hull as one number — the furthest it reached from
 * its own origin, applied in every direction at once. That makes a ship a
 * circle, and ships are not circles: the starter is nine units long and under
 * seven wide, so a circle around it is a third wider than the ship down both
 * flanks. You could be stopped by a deck your wing was clearly past, and the
 * error grows with every extension bolted on.
 *
 * A hull is drawn from a graph of joints and lines, so its real outline is
 * available and there is no good reason to throw it away.
 *
 * ── Why the convex hull rather than the outline itself ──────────────────────
 *
 * The graph's lines include internal walls — compartment dividers that are not
 * part of the silhouette at all — so "the outline" is not simply the line list.
 * Recovering the outer face from a line graph is a real piece of work, and it
 * would still hand back a shape that changes topology whenever a wall is added.
 *
 * The convex hull of the joints is stable, cheap, and never smaller than the
 * ship. It is generous only where a hull is genuinely concave, which for ships
 * built on this grid is a notch here and there rather than a horseshoe. That is
 * the right way to be wrong: a hull that collides slightly early is a hull you
 * can fly, and one that collides slightly late clips through walls.
 */
/**
 * Cross product of OA→OB and OA→OC.
 *
 * Positive when the three turn counter-clockwise, which is what the hull walk
 * below keeps and what decides the winding of the result.
 */
function turn(o, a, b) {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}
/**
 * The convex hull of a set of points, by monotone chain.
 *
 * Sorted by x then y, then swept once up the lower side and once back down the
 * upper. Collinear points are dropped — they add vertices to every later test
 * without changing the shape.
 */
export function convexHull(points) {
    const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    if (sorted.length < 3)
        return sorted;
    const half = (source) => {
        const chain = [];
        for (const point of source) {
            while (chain.length >= 2 && turn(chain[chain.length - 2], chain[chain.length - 1], point) <= 0) {
                chain.pop();
            }
            chain.push(point);
        }
        chain.pop();
        return chain;
    };
    const hull = [...half(sorted), ...half([...sorted].reverse())];
    return hull.length >= 3 ? hull : sorted;
}
/**
 * A hull's footprint, from the graph it is built out of.
 *
 * `floor` is the smallest a ship is allowed to be, and exists for the same
 * reason it does elsewhere: a graph with nothing in it must not collide with
 * nothing.
 */
export function shipFootprint(graph, thickness, floor) {
    const outline = convexHull(graph.joints.map((joint) => ({ x: joint.x, y: joint.y })));
    const square = [
        { x: -floor, y: -floor },
        { x: floor, y: -floor },
        { x: floor, y: floor },
        { x: -floor, y: floor },
    ];
    const shape = outline.length >= 3 ? outline : square;
    const reach = shape.reduce((furthest, p) => Math.max(furthest, Math.hypot(p.x, p.y)), 0);
    return { outline: shape, depth: thickness / 2, reach: Math.max(floor, reach) };
}
//# sourceMappingURL=footprint.js.map